import { useEffect, useRef } from "react";

export default function GalleryBannerCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const banner = canvas.parentElement;
    const context = canvas.getContext("2d");
    if (!context) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointer = { x: 0, y: 0, active: false };
    let width = 0;
    let height = 0;
    let particles = [];
    let frame = null;
    let previousTime = 0;
    let visible = true;

    function resize() {
      width = banner.clientWidth;
      height = banner.clientHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      particles = Array.from({ length: Math.min(55, Math.max(18, Math.floor(width / 24))) }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 12,
        vy: (Math.random() - 0.5) * 12,
      }));
      draw(0);
    }

    function draw(delta) {
      context.clearRect(0, 0, width, height);
      if (pointer.active && !motion.matches) {
        const glow = context.createRadialGradient(pointer.x, pointer.y, 0, pointer.x, pointer.y, 150);
        glow.addColorStop(0, "rgba(0,184,148,0.16)");
        glow.addColorStop(1, "rgba(0,184,148,0)");
        context.fillStyle = glow;
        context.fillRect(0, 0, width, height);
      }
      particles.forEach((particle, index) => {
        particle.x = (particle.x + particle.vx * delta + width) % width;
        particle.y = (particle.y + particle.vy * delta + height) % height;
        context.fillStyle = "rgba(167,243,208,0.5)";
        context.beginPath();
        context.arc(particle.x, particle.y, 1.8, 0, Math.PI * 2);
        context.fill();
        for (let next = index + 1; next < particles.length; next += 1) {
          const other = particles[next];
          const distance = Math.hypot(particle.x - other.x, particle.y - other.y);
          if (distance < 120) line(particle, other, (1 - distance / 120) * 0.18);
        }
        if (pointer.active && !motion.matches) {
          const distance = Math.hypot(particle.x - pointer.x, particle.y - pointer.y);
          if (distance < 170) line(particle, pointer, (1 - distance / 170) * 0.55);
        }
      });
    }

    function line(start, end, opacity) {
      context.strokeStyle = `rgba(104,225,215,${opacity})`;
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(start.x, start.y);
      context.lineTo(end.x, end.y);
      context.stroke();
    }

    function animate(time) {
      frame = null;
      const delta = previousTime ? Math.min((time - previousTime) / 1000, 0.05) : 0;
      previousTime = time;
      draw(delta);
      frame = requestAnimationFrame(animate);
    }

    function syncAnimation() {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      previousTime = 0;
      if (visible && !document.hidden && !motion.matches) frame = requestAnimationFrame(animate);
      else draw(0);
    }

    function movePointer(event) {
      if (event.pointerType === "touch") return;
      const bounds = canvas.getBoundingClientRect();
      pointer.x = event.clientX - bounds.left;
      pointer.y = event.clientY - bounds.top;
      pointer.active = true;
    }
    function leavePointer() { pointer.active = false; }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(banner);
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      syncAnimation();
    });
    visibilityObserver.observe(banner);
    banner.addEventListener("pointermove", movePointer);
    banner.addEventListener("pointerleave", leavePointer);
    document.addEventListener("visibilitychange", syncAnimation);
    motion.addEventListener("change", syncAnimation);
    resize();
    syncAnimation();

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
      banner.removeEventListener("pointermove", movePointer);
      banner.removeEventListener("pointerleave", leavePointer);
      document.removeEventListener("visibilitychange", syncAnimation);
      motion.removeEventListener("change", syncAnimation);
    };
  }, []);

  return <canvas ref={canvasRef} className="gallery-banner-canvas" aria-hidden="true" />;
}
